"use client";

import { useRef, useState, useCallback } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { Trash2, Plus } from "lucide-react";
import { BackendButton } from "@/app/components/backend/backend-ui";
import { BackendLabel } from "./backend-label";
import { ImagePreview } from "./image-upload/image-preview";
import { ImageMetadata } from "./image-upload/image-metadata";
import { DragOverlay } from "./image-upload/drag-overlay";
import { UploadActionButtons } from "./image-upload/upload-action-buttons";
import ImagePreviewLink from "./image-upload/image-preview-link";
import { useDragDrop } from "@/app/hooks/images/use-drag-drop";
import type { ImageTypeT, ImageEntityTypeT } from "@/app/types/images";

export type MultiImageItem = {
  id: string;
  file?: File; // For new uploads
  url: string; // For preview (File URL or existing image URL)
  altText: string;
  originalName: string;
  position: number;
  isExisting?: boolean; // Flag to distinguish existing from new images
};

export type BackendMultiImageUploadProps = {
  label: string;
  images: MultiImageItem[];
  onImagesChange: (images: MultiImageItem[]) => void;
  maxImages?: number;
  className?: string;
  acceptedFileTypes?: string;
  entityType: ImageEntityTypeT;
  entityId: string;
  imageType: ImageTypeT;
};

type ImageItemProps = {
  image: MultiImageItem;
  onDelete: (id: string) => void;
  isDragOver?: boolean;
};

function ImageItem({ image, onDelete, isDragOver }: ImageItemProps) {
  return (
    <div
      className={cn(
        "group relative aspect-square overflow-hidden rounded-md border bg-gray-50 transition-all hover:border-gray-300",
        isDragOver && "scale-105 border-blue-300 shadow-lg"
      )}
    >
      {/* Image */}
      <Image
        src={image.url}
        alt={image.altText || image.originalName}
        fill
        className="object-cover"
        draggable={false}
        sizes="(max-width: 768px) 25vw, (max-width: 1200px) 12.5vw, 8.33vw"
      />

      {/* Position Badge */}
      <div className="absolute top-2 left-2 flex h-6 w-6 items-center justify-center rounded-full bg-blue-500 text-xs font-semibold text-white shadow-sm">
        {image.position}
      </div>

      {/* Delete Button */}
      <BackendButton
        type="button"
        variant="secondary"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(image.id);
        }}
        className="absolute top-2 right-2 h-6 w-6 p-0 opacity-0 shadow-sm transition-opacity group-hover:opacity-100"
      >
        <Trash2 className="h-3 w-3" />
      </BackendButton>
    </div>
  );
}

/**
 * BackendMultiImageUpload - Reusable multi-image upload component
 *
 * Features:
 * - Drag & drop support for multiple images
 * - Local state management (saves on form submit)
 * - Position-based sorting via drag & drop
 * - Consistent styling with BackendImageUpload
 * - Support for existing and new images
 * - File validation and preview
 */
export function BackendMultiImageUpload({
  label,
  images,
  onImagesChange,
  maxImages = 20,
  className,
  acceptedFileTypes = "image/*",
  imageType,
}: BackendMultiImageUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const isUploading = false;
  const isDeleting = false;
  const currentImageUrl = null;
  const currentAltText = null;

  const handleImageDelete = () => {
    // Handle image deletion logic here
  };

  const handleFileSelect = useCallback(
    (files: File | File[]) => {
      const fileArray = Array.isArray(files) ? files : [files];
      const validFiles = fileArray.filter(
        (file) =>
          file.type.startsWith("image/") && file.size <= 10 * 1024 * 1024 // 10MB limit
      );

      if (validFiles.length === 0) return;

      const newImages: MultiImageItem[] = validFiles.map((file, index) => ({
        id: `temp-${Date.now()}-${index}`,
        file,
        url: URL.createObjectURL(file),
        altText: "",
        originalName: file.name,
        position: images.length + index + 1,
        isExisting: false,
      }));

      // Limit total images
      const totalImages = images.length + newImages.length;
      if (totalImages > maxImages) {
        const allowedNewImages = maxImages - images.length;
        newImages.splice(allowedNewImages);
      }

      onImagesChange([...images, ...newImages]);
    },
    [images, onImagesChange, maxImages]
  );

  const { isDragOver, dragHandlers } = useDragDrop({
    onFileSelect: handleFileSelect,
    disabled: false,
  });

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleFileSelect(Array.from(files));
    }
    // Reset input
    event.target.value = "";
  };

  const handleDeleteImage = useCallback(
    (imageId: string) => {
      const updatedImages = images
        .filter((img) => img.id !== imageId)
        .map((img, index) => ({ ...img, position: index + 1 }));

      onImagesChange(updatedImages);
    },
    [images, onImagesChange]
  );

  // Drag and Drop sorting
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", index.toString());
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (draggedIndex === null || draggedIndex === index) return;
    setDragOverIndex(index);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear if we're leaving the drop zone entirely
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newImages = [...images];
    const draggedImage = newImages[draggedIndex];

    // Remove dragged item
    newImages.splice(draggedIndex, 1);
    // Insert at new position
    newImages.splice(dropIndex, 0, draggedImage);

    // Update positions (1-based indexing)
    const updatedImages = newImages.map((img, index) => ({
      ...img,
      position: index + 1,
    }));

    onImagesChange(updatedImages);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  // Handle container click (avoid triggering on child clicks)
  const handleContainerClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // Don't trigger if clicking on the ArrowUpRight link
    if ((event.target as HTMLElement).closest("a")) {
      return;
    }
    triggerFileInput();
  };

  const hasImages = images.length > 0;
  const canAddMore = images.length < maxImages;

  return (
    <div className={cn("flex flex-col gap-1 sm:gap-1.5", className)}>
      <BackendLabel>{label}</BackendLabel>

      {/* Upload Area */}
      {!hasImages && (
        <div
          className={cn(
            "relative h-[90px] max-w-md cursor-pointer space-y-4 rounded-md border bg-[var(--c-card)] shadow-xs transition-colors hover:border-gray-300",
            isDragOver && "bg-secondary",
            isUploading && "pointer-events-none opacity-75"
          )}
          {...dragHandlers}
          onClick={handleContainerClick}
        >
          {/* Preview Link - only show if image is uploaded */}
          {currentImageUrl && (
            <ImagePreviewLink currentImageUrl={currentImageUrl} />
          )}
          {/* Image Preview Container */}
          <div className="h-full space-y-2">
            <div className="flex h-full w-full items-start justify-start gap-4">
              <ImagePreview
                imageUrl={currentImageUrl}
                altText={currentAltText}
                label={label}
              />
              <ImageMetadata
                isUploading={isUploading}
                currentImageUrl={currentImageUrl}
                imageType={imageType}
              />
            </div>
          </div>
          {/* Drag Overlay - show if image is dragged over the container */}
          {isDragOver && <DragOverlay />}
        </div>
      )}

      {/* Upload Action Buttons - Ersetzen & Löschen */}
      {!hasImages && (
        <UploadActionButtons
          isUploading={isUploading}
          isDeleting={isDeleting}
          hasImage={!!currentImageUrl}
          onUploadClick={triggerFileInput}
          onDeleteClick={handleImageDelete}
        />
      )}

      {/* Images Grid */}
      {hasImages && (
        <div className="space-y-3">
          <div className="grid grid-cols-4 gap-3 sm:grid-cols-6 md:grid-cols-8">
            {images
              .sort((a, b) => a.position - b.position) // Ensure correct order
              .map((image, index) => (
                <div
                  key={image.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  className={cn(
                    "cursor-move transition-opacity",
                    draggedIndex === index && "opacity-50"
                  )}
                >
                  <ImageItem
                    image={image}
                    onDelete={handleDeleteImage}
                    isDragOver={dragOverIndex === index}
                  />
                </div>
              ))}
          </div>

          {/* Add More Button - styled like BackendImageUpload */}
          {canAddMore && (
            <div className="mt-3 flex flex-row">
              <BackendButton
                type="button"
                size="xs"
                variant="secondary"
                className="justify-center font-normal [&_svg]:size-3"
                onClick={triggerFileInput}
              >
                <div className="flex items-center justify-center gap-1">
                  <Plus className="-translate-y-[0.5px]" />
                  <span>
                    Weitere Bilder ({images.length}/{maxImages})
                  </span>
                </div>
              </BackendButton>
            </div>
          )}
        </div>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={acceptedFileTypes}
        onChange={handleFileInputChange}
        className="hidden"
      />
    </div>
  );
}
